import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { Save, ArrowLeft, Loader2, Upload, X, Download, Eye } from 'lucide-react'
import { TEMPLATE_PRESETS, BORDER_STYLES, FONT_OPTIONS, DEFAULT_CONFIG } from './templatePresets'
import type { TemplateConfig } from './templatePresets'
import CertificatePreview from './CertificatePreview'

export default function CertificateTemplateEditor() {
  const { templateId } = useParams()
  const navigate = useNavigate()
  const isEditing = !!templateId
  const svgRef = useRef<SVGSVGElement>(null)

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [config, setConfig] = useState<TemplateConfig>({ ...DEFAULT_CONFIG })
  const [backgroundImageUrl, setBackgroundImageUrl] = useState<string | null>(null)
  const [isDefault, setIsDefault] = useState(false)
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(isEditing)
  const [uploading, setUploading] = useState(false)
  const [previewName, setPreviewName] = useState('Maria Lopez')
  const [showPresets, setShowPresets] = useState(!isEditing)

  useEffect(() => {
    if (isEditing) loadTemplate()
  }, [templateId])

  const loadTemplate = async () => {
    const { data } = await supabase
      .from('certificate_templates')
      .select('*')
      .eq('id', templateId as any)
      .single() as { data: any }

    if (data) {
      setName(data.name)
      setDescription(data.description || '')
      setConfig(data.design_config as TemplateConfig)
      setBackgroundImageUrl(data.background_image_url)
      setIsDefault(data.is_default)
    }
    setLoading(false)
  }

  const updateConfig = (key: keyof TemplateConfig, value: any) => {
    setConfig((prev) => ({ ...prev, [key]: value }))
  }

  const applyPreset = (preset: typeof TEMPLATE_PRESETS[0]) => {
    setConfig({ ...preset.config })
    if (!name) setName(preset.name)
    if (!description) setDescription(preset.description)
    setShowPresets(false)
  }

  const handleUploadBackground = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setUploading(true)
    const ext = file.name.split('.').pop()
    const path = `certificate-backgrounds/${Date.now()}.${ext}`

    const { error } = await supabase.storage.from('workshop-recordings').upload(path, file)
    if (error) {
      alert('Error al subir imagen: ' + error.message)
      setUploading(false)
      return
    }

    const { data: urlData } = supabase.storage.from('workshop-recordings').getPublicUrl(path)
    setBackgroundImageUrl(urlData.publicUrl)
    setUploading(false)
  }

  const handleSave = async () => {
    if (!name.trim()) {
      alert('El nombre es requerido')
      return
    }

    setSaving(true)
    const payload = {
      name: name.trim(),
      description: description.trim(),
      design_config: config,
      background_image_url: backgroundImageUrl,
      is_default: isDefault,
    }

    if (isEditing) {
      await (supabase.from('certificate_templates') as any).update(payload).eq('id', templateId)
    } else {
      await (supabase.from('certificate_templates') as any).insert(payload)
    }

    setSaving(false)
    navigate('/admin/certificates')
  }

  const handleDownloadPreview = async () => {
    if (!svgRef.current) return
    const svgData = new XMLSerializer().serializeToString(svgRef.current)
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' })
    const url = URL.createObjectURL(svgBlob)

    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = 1600
      canvas.height = 1132
      const ctx = canvas.getContext('2d')
      if (!ctx) return
      ctx.fillStyle = 'white'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      canvas.toBlob((blob) => {
        if (!blob) return
        const link = document.createElement('a')
        link.download = `certificado-preview.png`
        link.href = URL.createObjectURL(blob)
        link.click()
        URL.revokeObjectURL(link.href)
      }, 'image/png', 1.0)
      URL.revokeObjectURL(url)
    }
    img.src = url
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0055A4]" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/admin/certificates')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditing ? 'Editar plantilla' : 'Nueva plantilla'}
            </h1>
            <p className="text-gray-600">Personaliza el diseño del certificado</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleDownloadPreview}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 text-sm"
          >
            <Download className="w-4 h-4" />
            Descargar preview
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-[#0055A4] text-white rounded-lg hover:bg-[#004080] disabled:opacity-50 text-sm"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Guardar
          </button>
        </div>
      </div>

      {/* Preset selector */}
      {showPresets && (
        <div className="bg-white rounded-xl border shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900">Elige un modelo para empezar</h2>
            <button
              onClick={() => setShowPresets(false)}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Empezar en blanco
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {TEMPLATE_PRESETS.map((preset) => (
              <button
                key={preset.id}
                onClick={() => applyPreset(preset)}
                className="p-3 border rounded-xl hover:border-[#0055A4] hover:bg-[#0055A4]/5 transition-colors text-left group"
              >
                <div className="text-2xl mb-2">{preset.emoji}</div>
                <p className="text-sm font-medium text-gray-900 group-hover:text-[#0055A4]">
                  {preset.name}
                </p>
                <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{preset.description}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Configuration */}
        <div className="space-y-4">
          {/* Basic info */}
          <div className="bg-white rounded-xl border shadow-sm p-5 space-y-4">
            <h2 className="font-semibold text-gray-900">Información básica</h2>
            <div>
              <label htmlFor="template-name" className="block text-sm font-medium text-gray-700 mb-1">Nombre de la plantilla</label>
              <input id="template-name" type="text" value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
                placeholder="Ej: Certificado Unidad 1"
              />
            </div>
            <div>
              <label htmlFor="template-description" className="block text-sm font-medium text-gray-700 mb-1">Descripción (opcional)</label>
              <input id="template-description" type="text" value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
                placeholder="Ej: Para alumnos que completan la unidad 1"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is-default"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="rounded border-gray-300 text-[#0055A4] focus:ring-[#0055A4]"
              />
              <label htmlFor="is-default" className="text-sm text-gray-700">
                Plantilla por defecto
              </label>
            </div>

            {/* Preset switcher */}
            {!showPresets && (
              <button
                onClick={() => setShowPresets(true)}
                className="flex items-center gap-2 text-sm text-[#0055A4] hover:underline"
              >
                <Eye className="w-4 h-4" />
                Cambiar modelo base
              </button>
            )}
          </div>

          {/* Colors */}
          <div className="bg-white rounded-xl border shadow-sm p-5 space-y-4">
            <h2 className="font-semibold text-gray-900">Colores</h2>
            <div className="grid grid-cols-2 gap-4">
              {[
                { key: 'primaryColor' as const, label: 'Principal' },
                { key: 'secondaryColor' as const, label: 'Secundario' },
                { key: 'accentColor' as const, label: 'Acento' },
                { key: 'backgroundColor' as const, label: 'Fondo' },
                { key: 'textColor' as const, label: 'Texto' },
              ].map(({ key, label }) => (
                <div key={key}>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={config[key]}
                      onChange={(e) => updateConfig(key, e.target.value)}
                      className="w-8 h-8 rounded border cursor-pointer"
                    />
                    <input
                      type="text"
                      value={config[key]}
                      onChange={(e) => updateConfig(key, e.target.value)}
                      className="flex-1 px-2 py-1 border rounded text-xs font-mono"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Texts */}
          <div className="bg-white rounded-xl border shadow-sm p-5 space-y-4">
            <h2 className="font-semibold text-gray-900">Textos</h2>
            {[
              { key: 'headerText' as const, label: 'Encabezado', placeholder: 'NOMADLEXIS' },
              { key: 'titleText' as const, label: 'Título', placeholder: 'CERTIFICADO' },
              { key: 'subtitleText' as const, label: 'Subtítulo', placeholder: 'Este certificado se otorga a' },
              { key: 'achievementPlaceholder' as const, label: 'Logro', placeholder: 'Unidad 1 - Primeros Pasos' },
              { key: 'footerText' as const, label: 'Pie', placeholder: 'del Curso Intensivo de Francés Nivel A1' },
            ].map(({ key, label, placeholder }) => (
              <div key={key}>
                <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
                <input
                  type="text"
                  value={config[key]}
                  onChange={(e) => updateConfig(key, e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
                  placeholder={placeholder}
                />
              </div>
            ))}
          </div>

          {/* Style options */}
          <div className="bg-white rounded-xl border shadow-sm p-5 space-y-4">
            <h2 className="font-semibold text-gray-900">Estilo</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="border-style" className="block text-xs font-medium text-gray-600 mb-1">Tipo de borde</label>
                <select id="border-style" value={config.borderStyle}
                  onChange={(e) => updateConfig('borderStyle', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                >
                  {BORDER_STYLES.map((b) => (
                    <option key={b.value} value={b.value}>{b.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="font-family" className="block text-xs font-medium text-gray-600 mb-1">Tipografia</label>
                <select id="font-family" value={config.fontFamily}
                  onChange={(e) => updateConfig('fontFamily', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                >
                  {FONT_OPTIONS.map((f) => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex flex-wrap gap-4">
              {[
                { key: 'showFlag' as const, label: 'Bandera francesa' },
                { key: 'showSeal' as const, label: 'Sello' },
                { key: 'showCornerDecorations' as const, label: 'Esquinas decoradas' },
              ].map(({ key, label }) => (
                <label key={key} className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={config[key]}
                    onChange={(e) => updateConfig(key, e.target.checked)}
                    className="rounded border-gray-300 text-[#0055A4] focus:ring-[#0055A4]"
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>

          {/* Background image */}
          <div className="bg-white rounded-xl border shadow-sm p-5 space-y-4">
            <h2 className="font-semibold text-gray-900">Imagen de fondo (opcional)</h2>
            <p className="text-xs text-gray-500">
              Sube una imagen de fondo y el sistema colocara los textos sobre ella. Tamano recomendado: 1600x1132px.
            </p>

            {backgroundImageUrl ? (
              <div className="relative">
                <img
                  src={backgroundImageUrl}
                  alt="Fondo"
                  className="w-full rounded-lg border"
                />
                <button
                  onClick={() => setBackgroundImageUrl(null)}
                  className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center gap-2 p-6 border-2 border-dashed rounded-xl cursor-pointer hover:border-[#0055A4] hover:bg-[#0055A4]/5 transition-colors">
                {uploading ? (
                  <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
                ) : (
                  <Upload className="w-8 h-8 text-gray-400" />
                )}
                <span className="text-sm text-gray-500">
                  {uploading ? 'Subiendo...' : 'Haz clic para subir imagen'}
                </span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleUploadBackground}
                  className="hidden"
                  disabled={uploading}
                />
              </label>
            )}
          </div>
        </div>

        {/* Right: Live Preview */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border shadow-sm p-5 sticky top-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-900">Vista previa</h2>
              <div className="flex items-center gap-2">
                <label htmlFor="preview-name" className="text-xs text-gray-500">Nombre:</label>
                <input id="preview-name" type="text" value={previewName}
                  onChange={(e) => setPreviewName(e.target.value)}
                  className="px-2 py-1 border rounded text-xs w-36"
                  placeholder="Nombre alumno"
                />
              </div>
            </div>
            <div className="border rounded-lg overflow-hidden bg-gray-50">
              <CertificatePreview
                ref={svgRef}
                config={config}
                studentName={previewName}
                backgroundImageUrl={backgroundImageUrl}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
